/**
 * Browser streaming store (Neko + WebRTC).
 *
 * Two orthogonal concerns in one store:
 *
 *   1. WebRTC stream lifecycle — populated by browserHandlers on
 *      BROWSER_STREAM_READY, cleared on BROWSER_STREAM_STOPPED. Provides
 *      the Neko WebSocket URL + ICE servers for useNekoClient.
 *
 *   2. Tab list — populated by browserHandlers on BROWSER_TABS_UPDATED
 *      (full snapshots from the sandbox). The sandbox is the single source
 *      of truth; we never mutate `tabs` optimistically.
 *
 * Not persisted — the server is SOT and re-emits both streams on reconnect.
 */
import { create } from "zustand";
import type {
  BrowserEmbeddedReadyPayload,
  BrowserExtensionReadyPayload,
  BrowserLocalReadyPayload,
  BrowserLocalStoppedPayload,
  BrowserTab,
  BrowserTabsPayload,
  BrowserViewStatePayload,
} from "@bash/shared";
import { isPaneVisibilityEnabled } from "../lib/page-visibility";
import { removeSidecarFrame } from "../lib/frozen-frame-sidecar";

/**
 * One tab in the embedded browser's own strip (EMBEDDED_BROWSER_V1).
 * Snapshots arrive on EVENTS.BROWSER_TABS (local/embedded lane only) — the
 * BROWSER_TABS_UPDATED tab shape plus a required `active` flag marking the
 * tab the runner's session is bound to (what the pane shows).
 */
export type EmbeddedBrowserTab = BrowserTabsPayload["tabs"][number];

export type BrowserConnectionStatus =
  | "idle" // no session
  | "waking" // user clicked toggle — sandbox is waking
  | "ready" // server says a session is available
  | "connecting" // canvas component is opening the WebSocket
  | "streaming" // WebSocket is open, frames are flowing
  | "sleeping" // server-side 24s idle timer fired — pane stays mounted with overlay
  | "suspended" // P2 viewer-lease: stream frozen by hibernation — last frame stays, auto-resumes
  | "error"; // WebSocket errored — user action (retry) required

/**
 * What the browser pane is showing right now, as ONE word for the desktop's
 * window chrome. The canvas is the only place that knows whether pixels have
 * painted, so it reports and the title bar reads. 'idle' = no pane mounted.
 */
export type BrowserPaneMood =
  "idle" | "connecting" | "live" | "asleep" | "error";

type BrowserState = {
  // ─── Server-owned view snapshot (BROWSER_VIEW_MACHINE_V1) ────────
  /**
   * The last BROWSER_VIEW_STATE snapshot from the server's view machine —
   * rendered verbatim by BrowserCanvas when the flag is ON. Null until the
   * first snapshot (and always null when the flag is OFF, where nothing reads
   * it). This is the SOLE view truth under the flag; the legacy stream fields
   * below stay dark because their handlers early-return.
   */
  viewSnapshot: BrowserViewStatePayload | null;
  /**
   * True while the snapshot above cannot be trusted to describe the box RIGHT
   * NOW: the socket went down, so whatever the server did next — a stand-down,
   * a hibernate — was said to a dead socket (PANE_VISIBILITY_V1, work holds the
   * box §5.1). Set on disconnect, cleared by the next snapshot the reconnect
   * replays. Only the pane's dial reads it: closing the laptop lid ends with a
   * `visible` edge that re-runs the neko effect, and without this that edge
   * dials the old `live` creds into a preview whose box has hibernated —
   * thawing a paid container for a stream nobody asked to resume.
   */
  snapshotStale: boolean;
  /**
   * How many snapshots the server has handed us, ever. Not a clock and not a
   * state — the one thing a caller needs to tell "the server answered me just
   * now" from "I am still holding what I held before I asked". `since` cannot
   * do it (a level re-emit repeats the last transition's stamp, by design) and
   * neither can the state (the answer may be the same word).
   *
   * It exists because the pane may not ACT on a snapshot it has not had
   * re-confirmed: re-dialling the neko wire is an HTTP request at the box's
   * preview URL, and that RESUMES a frozen box (docs/learnings/blaxel-sdk-tips
   * "a bare HTTP request to the preview URL wakes it"). A dial decided from a
   * snapshot the box may have outlived is a wake nobody asked for and a bill
   * nobody agreed to — the box-awake-only-while-there-is-work law.
   */
  viewSnapshotSeq: number;
  /**
   * The last decoded browser frame per task, captured the instant a session
   * suspended (BROWSER_VIEW_MACHINE_V1). Keyed by task because the Window
   * lingers past done and un-mounts on pane-close: a component-local frame
   * would die there, so the reopened task would show a blank card instead of
   * its last frozen frame (spec Drill 8). ~100-300KB/task, tab-scoped.
   */
  frozenFrameByTask: Record<string, string>;

  // ─── Stream ─────────────────────────────────────────────────────
  sessionId: string | null;
  /**
   * Which transport minted the current session — drives which client
   * connects. 'embedded' (EMBEDDED_BROWSER_V1) is the shell's own CEF pane:
   * nothing to dial, the surface is a punchout driven by shell commands.
   * 'extension' means the agent is driving the operator's REAL Chrome: there is
   * no in-app surface, so the pane shows an honest "working in your Chrome" state
   * instead of a stale prior body (the false-success bug).
   */
  source: "neko" | "local" | "embedded" | "extension" | null;
  /** Local in-app browser (LOCAL_BROWSER_V1) loopback view server + auth. */
  localWsUrl: string | null;
  localToken: string | null;
  width: number;
  height: number;
  status: BrowserConnectionStatus;
  paneMood: BrowserPaneMood;
  /**
   * Browser handoff (spec 2026-09-02): when the person last asked to open the
   * browser from the story (openBrowserForTask). The desktop's lock veil and
   * the handoff card read it to say "waking" from the click frame, before
   * presence catches up; it clears when the wake lands or dies of old age.
   */
  wakeRequestedAt: number | null;
  hasBrowserSession: boolean;
  /** Alias used by legacy streamingHandlers — same value as sessionId. */
  streamSessionId: string | null;
  /** True while the agent is actively driving the shared browser. UX-only. */
  isAgentControlling: boolean;
  /** Timestamp of last agent activity — surfaces a "just happened" pulse. */
  lastActivityAt: number | null;
  /** Current agent action description (e.g., "Clicking search button"). */
  agentAction: string | null;

  // ─── Per-task panel visibility ──────────────────────────────────
  /**
   * Set of taskIds that currently have the browser panel open in their UI.
   * The browser stream is a SHARED sandbox resource (one Chrome per user),
   * but panel VISIBILITY is owned per-task — opening in task A must not
   * leak into task B's layout. The backend session is alive iff this set
   * is non-empty; closing the last task triggers BROWSER_CLOSE_SESSION
   * server-side so the sandbox can quiesce.
   */
  panelOpenTaskIds: Set<string>;
  /**
   * Browser-fold: tasks whose browser surface folded into the parked chip.
   * A task is in AT MOST one of panelOpenTaskIds / parkedBrowserTaskIds —
   * parkPanelForTask moves it here, unparkPanelForTask / openPanelForTask
   * move it back. Membership here renders the chip (unless dismissed) and,
   * deliberately, does NOT re-assert server pane demand on reconnect: a
   * parked browser stays parked until the user or the agent opens it.
   */
  parkedBrowserTaskIds: Set<string>;
  /**
   * Tasks whose parked chip the user dismissed with ✕ — sticky per-task, the
   * same shape as computerStore's hasUserClosed. Cleared by an explicit open
   * (chip Undo→open, wake button) or by a REAL browser tool fire
   * (openPanelForTask), so "dismiss" means "stop showing me the parked
   * browser", never "never show me the browser".
   */
  dismissedChipTaskIds: Set<string>;
  /**
   * One-shot flag — false on a fresh page load, true once any user action
   * (open/close toggle) OR the first sandbox tab snapshot has reconciled
   * `panelOpenTaskIds`. Without this, deep-link / refresh of a task that
   * already has a live browser session leaves the pane invisible: the
   * server re-emits BROWSER_STREAM_READY (so `hasBrowserSession=true`),
   * but `AGENT_TOOL_USE_START` (which is what would normally populate
   * `panelOpenTaskIds`) is NOT replayed on reconnect — by design, to
   * prevent spurious panel openings every reconnect. Result: the gate
   * `panelOpenTaskIds.has(currentTaskId) && hasBrowserSession` stays false
   * until the agent fires its NEXT browser tool. Users see a "black pane"
   * (actually: no pane at all) while the agent is clearly working.
   *
   * The fix: on the first BROWSER_TABS_UPDATED snapshot received after
   * mount, treat tab-ownership as the source of truth for panel intent —
   * for every tab with `taskId !== null`, mark that task's panel open.
   * After that one reconciliation, panelOpenTaskIds belongs to the user
   * (their toggle is sticky; later tab snapshots don't re-open closed
   * panels).
   */
  _panelsRehydratedFromTabs: boolean;

  // ─── Tabs ───────────────────────────────────────────────────────
  /** Full tab list from the sandbox — always a complete snapshot. */
  tabs: BrowserTab[];
  /**
   * Embedded-lane tab strip — the MERGE of the runner's EVENTS.BROWSER_TABS
   * snapshots (title/url/favicon/taskId truth) with the shell's
   * `embedded-browser://tabs` event (the authoritative `active` marker:
   * visibility is a shell fact CDP enumeration cannot tell). This is what
   * the chrome renders; the raw slices live in the two fields below.
   */
  embeddedTabs: EmbeddedBrowserTab[];
  /** Last runner snapshot, unmerged — re-merged when either source updates. */
  _embeddedRunnerTabs: EmbeddedBrowserTab[];
  /** Last shell tab-set event, unmerged; null before the first. Rows carry
   *  the shell's live title/url from newer shells (its own CEF callbacks). */
  _embeddedShellTabs: EmbeddedShellTab[] | null;
  /**
   * Which tab the user is currently watching on the canvas. Null means
   * "default to the first tab in `tabs`" — useBrowserStream picks that up.
   * Pure UI state; never sent to the server.
   */
  selectedTargetId: string | null;
  /**
   * True when `selectedTargetId` came from an explicit user click (via
   * `selectTab`). False when it was auto-picked by `setTabs` fallback
   * logic. Explicit picks are preserved across snapshots even when the
   * agent opens new task-owned tabs; automatic picks get replaced so the
   * user sees the agent's latest work.
   */
  selectionIsExplicit: boolean;

  // ─── Stream actions ─────────────────────────────────────────────
  /**
   * Mirror a BROWSER_VIEW_STATE snapshot (BROWSER_VIEW_MACHINE_V1). Monotonic
   * `since` guard: a snapshot older than the stored one is dropped (a late,
   * out-of-order transition); an EQUAL-since snapshot is accepted so a
   * same-state level re-emit can refresh re-minted creds in place.
   */
  applyViewSnapshot: (payload: BrowserViewStatePayload) => void;
  /** The socket went down — what the store holds is a snapshot of a past box.
   *  No-op with PANE_VISIBILITY_V1 off, so today's path never sees the field
   *  move and the pane keeps dialing exactly as it does now. */
  markSnapshotStale: () => void;
  /** Store the last decoded frame for a task (captured on suspend). */
  captureFrozenFrame: (taskId: string, dataUrl: string) => void;
  /** Drop a task's frozen frame (on resume / stop / a fresh live stream). */
  clearFrozenFrame: (taskId: string) => void;
  /** Optimistically open the panel while sandbox wakes. */
  setWaking: () => void;
  noteWakeRequested: (at: number | null) => void;
  /** Local in-app browser (LOCAL_BROWSER_V1). */
  setLocalReady: (payload: BrowserLocalReadyPayload) => void;
  /**
   * Embedded browser (EMBEDDED_BROWSER_V1) READY — the shell owns the
   * Chromium surface, so there is no wsUrl/token/viewport to store; the
   * lane just flips live. Stopped rides setLocalStopped (sessionId match).
   */
  setEmbeddedReady: (payload: BrowserEmbeddedReadyPayload) => void;
  /**
   * Extension body active — the agent is driving the operator's real Chrome.
   * No surface to render; the pane shows an honest "working in your Chrome"
   * state so it never presents a stale in-app body as live. Stopped rides
   * setLocalStopped (sessionId match), same as the embedded lane.
   */
  setExtensionActive: (payload: BrowserExtensionReadyPayload) => void;
  /** Local in-app browser (LOCAL_BROWSER_V1) stop: idle sleeps the pane, anything else resets it. */
  setLocalStopped: (payload: BrowserLocalStoppedPayload) => void;
  /** P2 viewer-lease: freeze the stream (hibernation) — creds + pane survive. */
  /** P2 viewer-lease: un-freeze after hibernate-resume (back to 'ready'). */
  setStatus: (status: BrowserConnectionStatus) => void;
  /** Reported by the mounted BrowserCanvas (one writer); read by the desktop window. */
  setPaneMood: (mood: BrowserPaneMood) => void;
  setAgentControlling: (v: boolean) => void;
  notifyBrowserActivity: () => void;
  reset: () => void;

  // ─── Per-task panel actions ─────────────────────────────────────
  /** Returns true if the panel was the FIRST opened (caller should request session). */
  openPanelForTask: (taskId: string) => boolean;
  /** Returns true if the panel was the LAST closed (caller should request close). */
  closePanelForTask: (taskId: string) => boolean;
  isPanelOpenForTask: (taskId: string) => boolean;
  /**
   * Fold this task's browser surface into the parked chip (browser-fold):
   * the panel claim moves panelOpenTaskIds → parkedBrowserTaskIds so the
   * Window unmounts and the chip renders in its place. Returns true if the
   * panel was the LAST open (caller should release the server-side pane
   * demand, exactly like closePanelForTask).
   */
  parkPanelForTask: (taskId: string) => boolean;
  /**
   * Expand a parked chip back into the open panel (chip click). Clears any
   * dismissal — an explicit open always wins over a past ✕. Returns true if
   * the panel was the FIRST opened (caller should re-assert pane demand).
   */
  unparkPanelForTask: (taskId: string) => boolean;
  /** ✕ on the chip — sticky per-task, cleared only by an explicit open or a
   *  real browser tool fire (openPanelForTask clears it). */
  dismissParkedChip: (taskId: string) => void;
  /** Undo link in the post-dismiss whisper. */
  undoDismissParkedChip: (taskId: string) => void;
  /**
   * Drop a deleted task's entry from `panelOpenTaskIds`. If that emptied
   * the set while `hasBrowserSession` was still true, ALSO surface that
   * the session leaked — full reset, returns `wasLastAndSessionLive=true`
   * so the caller can decide whether to tell the server to close.
   *
   * Without this, deleting a task while its panel was open leaves a
   * phantom id in the set. Subsequent `closePanelForTask` calls in
   * other tasks return `wasLast=false`, `reset()` never fires, and
   * the 'browsing' display overlay sticks forever.
   */
  cleanupForDeletedTask: (taskId: string) => { wasLastAndSessionLive: boolean };

  // ─── Tab actions ────────────────────────────────────────────────
  setTabs: (tabs: BrowserTab[]) => void;
  selectTab: (targetId: string) => void;
  setEmbeddedTabs: (tabs: EmbeddedBrowserTab[]) => void;
  /** Shell `embedded-browser://tabs` event — the active-marker overlay. */
  setEmbeddedShellTabs: (tabs: EmbeddedShellTab[]) => void;
};

const INITIAL = {
  viewSnapshot: null as BrowserViewStatePayload | null,
  snapshotStale: false,
  viewSnapshotSeq: 0,
  frozenFrameByTask: {} as Record<string, string>,
  sessionId: null,
  source: null as "neko" | "local" | "embedded" | null,
  localWsUrl: null as string | null,
  localToken: null as string | null,
  width: 1920,
  height: 1080,
  status: "idle" as BrowserConnectionStatus,
  paneMood: "idle" as BrowserPaneMood,
  wakeRequestedAt: null as number | null,
  hasBrowserSession: false,
  streamSessionId: null,
  isAgentControlling: false,
  lastActivityAt: null,
  agentAction: null as string | null,
  panelOpenTaskIds: new Set<string>(),
  parkedBrowserTaskIds: new Set<string>(),
  dismissedChipTaskIds: new Set<string>(),
  _panelsRehydratedFromTabs: false,
  tabs: [] as BrowserTab[],
  embeddedTabs: [] as EmbeddedBrowserTab[],
  _embeddedRunnerTabs: [] as EmbeddedBrowserTab[],
  _embeddedShellTabs: null as EmbeddedShellTab[] | null,
  selectedTargetId: null as string | null,
  selectionIsExplicit: false,
};

export const useBrowserStore = create<BrowserState>((set, get) => ({
  ...INITIAL,

  applyViewSnapshot: (payload) => {
    // Drop a stale (older) transition; accept equal-since so a same-state level
    // re-emit refreshes re-minted creds in place.
    const prev = get().viewSnapshot;
    if (prev && payload.since < prev.since) return;
    // A wake that failed hands Open back: the story's wake stamp clears with
    // the error snapshot instead of standing for its 20 s (review 09-02).
    if (payload.state === "error" && get().wakeRequestedAt !== null)
      set({ wakeRequestedAt: null });
    // This snapshot is the SOLE view truth, so it also drives the mount-gate
    // field StoryView reads (hasBrowserSession). A snapshot from the server is
    // the box's own word, so it ends any staleness a socket drop declared
    // (PANE_VISIBILITY_V1) — the reconnect's replay is exactly this call.
    // Every ACCEPTED snapshot advances the counter, including a byte-equal
    // level re-emit: "the server spoke again" is the fact a caller waits on,
    // and a repeat is the server confirming, not staying silent.
    const seq = get().viewSnapshotSeq + 1;
    set({
      viewSnapshot: payload,
      snapshotStale: false,
      viewSnapshotSeq: seq,
      hasBrowserSession: payload.state !== "hidden",
    });
  },

  markSnapshotStale: () => {
    if (!isPaneVisibilityEnabled()) return;
    set({ snapshotStale: true });
  },

  captureFrozenFrame: (taskId, dataUrl) =>
    set({
      frozenFrameByTask: { ...get().frozenFrameByTask, [taskId]: dataUrl },
    }),

  clearFrozenFrame: (taskId) => {
    const next = { ...get().frozenFrameByTask };
    delete next[taskId];
    set({ frozenFrameByTask: next });
  },

  setWaking: () =>
    set({
      status: "waking",
      hasBrowserSession: true, // open the panel immediately
    }),

  noteWakeRequested: (at) => {
    if (get().wakeRequestedAt !== at) set({ wakeRequestedAt: at });
  },

  setLocalReady: (payload) =>
    set({
      sessionId: payload.sessionId,
      source: "local",
      streamSessionId: payload.sessionId,
      localWsUrl: payload.wsUrl,
      localToken: payload.token,
      width: payload.width,
      height: payload.height,
      status: "ready",
      hasBrowserSession: true,
    }),

  setEmbeddedReady: (payload) =>
    set({
      sessionId: payload.sessionId,
      source: "embedded",
      streamSessionId: payload.sessionId,
      status: "ready",
      hasBrowserSession: true,
    }),

  setExtensionActive: (payload) =>
    set({
      sessionId: payload.sessionId,
      source: "extension",
      streamSessionId: payload.sessionId,
      status: "ready",
      hasBrowserSession: true,
    }),

  setLocalStopped: (payload) => {
    // Stale-event guard — if the stopped sessionId doesn't match the current
    // session, another session already took over and we shouldn't clobber it.
    if (get().sessionId !== payload.sessionId) return;

    // Idle auto-close keeps the panel mounted in 'sleeping' (the pane shows a
    // Wake button); any other reason is a full reset and the pane hides.
    if (payload.reason === "idle") {
      const panelOpenTaskIds = get().panelOpenTaskIds;
      set({
        ...INITIAL,
        panelOpenTaskIds,
        hasBrowserSession: true,
        status: "sleeping",
      });
      return;
    }
    set({ ...INITIAL });
  },

  setPaneMood: (paneMood) => {
    if (get().paneMood !== paneMood) set({ paneMood });
  },

  setStatus: (status) => set({ status }),

  setAgentControlling: (v) => set({ isAgentControlling: v }),

  notifyBrowserActivity: () => set({ lastActivityAt: Date.now() }),

  reset: () => {
    // Preserve panelOpenTaskIds across stream resets — panel visibility is
    // a USER intent, independent of whether the underlying stream is up.
    // After a stream tear-down + reopen (e.g. handleReconnect), the user
    // still wants the panel visible for the same task they had it open in.
    //
    // Also preserve `_panelsRehydratedFromTabs`. The flag tracks "has the
    // user already exercised panel intent in this page lifetime?" — a
    // property of the SESSION, not the stream. Resetting it would let a
    // stream-restart silently re-open panels the user had explicitly
    // closed (e.g. user closes pane → server forces restart → tabs come
    // back → reconciliation re-opens the closed pane). Only a true page
    // reload should rearm rehydration, and that comes "free" via a fresh
    // store instance.
    // parked/dismissed ride along for the same reason: browser-fold state is
    // USER intent about panel visibility, not stream state.
    const {
      panelOpenTaskIds,
      parkedBrowserTaskIds,
      dismissedChipTaskIds,
      _panelsRehydratedFromTabs,
    } = get();
    set({
      ...INITIAL,
      panelOpenTaskIds,
      parkedBrowserTaskIds,
      dismissedChipTaskIds,
      _panelsRehydratedFromTabs,
    });
  },

  openPanelForTask: (taskId) => {
    // A real open — agent browser tool fire or explicit user action — always
    // overrides browser-fold state: the chip un-parks and a past ✕ is
    // forgiven (dismiss means "stop showing the PARKED browser", and this
    // browser is no longer parked). Cleared even on the has-already branch
    // below so a dismissed chip can't linger beside an open panel.
    const { parkedBrowserTaskIds, dismissedChipTaskIds } = get();
    if (parkedBrowserTaskIds.has(taskId) || dismissedChipTaskIds.has(taskId)) {
      const parked = new Set(parkedBrowserTaskIds);
      const dismissed = new Set(dismissedChipTaskIds);
      parked.delete(taskId);
      dismissed.delete(taskId);
      set({ parkedBrowserTaskIds: parked, dismissedChipTaskIds: dismissed });
    }
    const prev = get().panelOpenTaskIds;
    if (prev.has(taskId)) return false;
    const next = new Set(prev);
    next.add(taskId);
    // Any explicit user toggle consumes the auto-rehydrate budget — from
    // here on, panelOpenTaskIds is owned by the user, not the tab snapshot.
    set({ panelOpenTaskIds: next, _panelsRehydratedFromTabs: true });
    // First task to open the panel — caller should request the session.
    return prev.size === 0;
  },

  parkPanelForTask: (taskId) => {
    const prev = get().panelOpenTaskIds;
    if (!prev.has(taskId)) return false;
    const next = new Set(prev);
    next.delete(taskId);
    const parked = new Set(get().parkedBrowserTaskIds);
    parked.add(taskId);
    // Folding is a user-flow verdict like closing — a later tab snapshot
    // must not silently re-open the pane the send just folded.
    set({
      panelOpenTaskIds: next,
      parkedBrowserTaskIds: parked,
      _panelsRehydratedFromTabs: true,
    });
    return next.size === 0;
  },

  unparkPanelForTask: (taskId) => {
    const parked = new Set(get().parkedBrowserTaskIds);
    if (!parked.has(taskId)) return false;
    // openPanelForTask owns the shared open semantics (clears parked +
    // dismissed, stamps rehydrate ownership, returns wasFirst).
    return get().openPanelForTask(taskId);
  },

  dismissParkedChip: (taskId) => {
    if (!get().parkedBrowserTaskIds.has(taskId)) return;
    const dismissed = new Set(get().dismissedChipTaskIds);
    dismissed.add(taskId);
    set({ dismissedChipTaskIds: dismissed });
  },

  undoDismissParkedChip: (taskId) => {
    const prev = get().dismissedChipTaskIds;
    if (!prev.has(taskId)) return;
    const dismissed = new Set(prev);
    dismissed.delete(taskId);
    set({ dismissedChipTaskIds: dismissed });
  },

  closePanelForTask: (taskId) => {
    const prev = get().panelOpenTaskIds;
    if (!prev.has(taskId)) return false;
    const next = new Set(prev);
    next.delete(taskId);
    // Closing is also a user verdict — don't let a later tab snapshot
    // silently re-open the pane the user just hid.
    set({ panelOpenTaskIds: next, _panelsRehydratedFromTabs: true });
    // Last task to close the panel — caller should tear down the session.
    return next.size === 0;
  },

  isPanelOpenForTask: (taskId) => get().panelOpenTaskIds.has(taskId),

  cleanupForDeletedTask: (taskId) => {
    // A deleted task's screen must not ghost on disk either.
    removeSidecarFrame(taskId);
    // A deleted task's fold state must not ghost around (a phantom parked id
    // would render a chip for a task that no longer exists on revisit-by-id).
    const { parkedBrowserTaskIds, dismissedChipTaskIds } = get();
    if (parkedBrowserTaskIds.has(taskId) || dismissedChipTaskIds.has(taskId)) {
      const parked = new Set(parkedBrowserTaskIds);
      const dismissed = new Set(dismissedChipTaskIds);
      parked.delete(taskId);
      dismissed.delete(taskId);
      set({ parkedBrowserTaskIds: parked, dismissedChipTaskIds: dismissed });
    }
    const prev = get().panelOpenTaskIds;
    if (!prev.has(taskId)) return { wasLastAndSessionLive: false };
    const next = new Set(prev);
    next.delete(taskId);
    const wasLastAndSessionLive = next.size === 0 && get().hasBrowserSession;
    if (wasLastAndSessionLive) {
      // Last panel-bearing task vanished and the session was still
      // marked live. Treat as a leaked-session state: full reset
      // (clears hasBrowserSession so 'browsing' overlay drops) but
      // keep the now-empty panelOpenTaskIds so reset's preserve
      // semantics stay consistent — and OTHER tasks' fold state, which
      // this task's deletion says nothing about.
      const {
        parkedBrowserTaskIds: parkedNow,
        dismissedChipTaskIds: dismissedNow,
      } = get();
      set({
        ...INITIAL,
        panelOpenTaskIds: next,
        parkedBrowserTaskIds: parkedNow,
        dismissedChipTaskIds: dismissedNow,
      });
    } else {
      set({ panelOpenTaskIds: next });
    }
    return { wasLastAndSessionLive };
  },

  setTabs: (tabs) => {
    // Selection priority:
    //
    // 1. If the user has EVER explicitly selected a tab, that pick is
    //    locked in until they click a different tab or the tab is closed
    //    by the user themselves via the × button. Heartbeat snapshots
    //    can never revert it. This is the fix for the drift bug where
    //    rapid agent navigations kept auto-focusing stale tabs behind
    //    the user's back.
    //
    // 2. Else if a task-owned tab appeared that wasn't in the previous
    //    snapshot → auto-focus it. This is "agent just opened a new tab",
    //    surface it automatically.
    //
    // 3. Else if the automatic selection still exists → keep it.
    //
    // 4. Else fall back to: first task-owned tab > first tab > null.
    const prev = get();

    // ── Cold-load panel rehydration ────────────────────────────────
    // On the very first snapshot per fresh store (page reload / deep-
    // link), reconcile `panelOpenTaskIds` from tab ownership: any tab
    // with a non-null taskId means the agent has done browser work for
    // that task, so its pane should be visible. This is the ONLY path
    // that re-opens a panel without a user click — protected by the
    // one-shot `_panelsRehydratedFromTabs` flag so later snapshots
    // (which arrive every ~2s while a session is live) cannot silently
    // re-open a pane the user just closed.
    let rehydratedPanelIds: Set<string> | null = null;
    if (!prev._panelsRehydratedFromTabs) {
      const merged = new Set(prev.panelOpenTaskIds);
      let added = false;
      for (const t of tabs) {
        if (t.taskId && !merged.has(t.taskId)) {
          merged.add(t.taskId);
          added = true;
        }
      }
      if (added) rehydratedPanelIds = merged;
    }
    const panelPatch = !prev._panelsRehydratedFromTabs
      ? rehydratedPanelIds
        ? {
            panelOpenTaskIds: rehydratedPanelIds,
            _panelsRehydratedFromTabs: true,
          }
        : { _panelsRehydratedFromTabs: true }
      : {};

    // Rule 1: explicit selection is sticky WHILE ITS TAB EXISTS. The
    // snapshot is the single source of truth: a pinned id absent from it
    // is a DEAD target (tab closed, or the whole engine restarted and
    // reminted ids) and can never come back — keeping it left the surface
    // watching a dead target forever ("Reconnecting…" while the agent
    // worked in a fresh tab; live-drill bug). Clear the stale selection
    // (pin included) so the canvas falls back to tabs[0] and re-watches
    // a live tab.
    if (prev.selectionIsExplicit) {
      const stillThere =
        prev.selectedTargetId &&
        tabs.some((t) => t.targetId === prev.selectedTargetId);
      if (!stillThere) {
        set({
          tabs,
          selectedTargetId: null,
          selectionIsExplicit: false,
          ...panelPatch,
        });
        return;
      }
      set({
        tabs,
        selectedTargetId: prev.selectedTargetId,
        selectionIsExplicit: true,
        ...panelPatch,
      });
      return;
    }

    // Rule 2: auto-focus newly-appeared task-owned tabs.
    const currentTargetIds = new Set(prev.tabs.map((t) => t.targetId));
    const newTaskTab = tabs.find(
      (t) => t.taskId !== null && !currentTargetIds.has(t.targetId)
    );
    if (newTaskTab) {
      set({
        tabs,
        selectedTargetId: newTaskTab.targetId,
        selectionIsExplicit: false,
        ...panelPatch,
      });
      return;
    }

    // Rule 3: keep the automatic selection if it still exists.
    const current = prev.selectedTargetId;
    const stillThere = current && tabs.some((t) => t.targetId === current);
    if (stillThere) {
      set({ tabs, selectionIsExplicit: false, ...panelPatch });
      return;
    }

    // Rule 4: fall back to first task-owned tab, then first tab, then null.
    const firstTaskTab = tabs.find((t) => t.taskId !== null)?.targetId;
    const fallback = firstTaskTab ?? tabs[0]?.targetId ?? null;
    set({
      tabs,
      selectedTargetId: fallback,
      selectionIsExplicit: false,
      ...panelPatch,
    });
  },

  selectTab: (targetId) => {
    // Refuse unknown tabs — prevents the UI from pinning to a target that
    // doesn't exist yet (would produce a 404 on the cast WS).
    if (!get().tabs.some((t) => t.targetId === targetId)) return;
    set({ selectedTargetId: targetId, selectionIsExplicit: true });
  },

  // Embedded strip is display-only state: each source replaces its own slice
  // wholesale (runner snapshot / shell tab-set), and the strip re-merges.
  setEmbeddedTabs: (tabs) =>
    set({
      _embeddedRunnerTabs: tabs,
      embeddedTabs: mergeEmbeddedTabs(tabs, get()._embeddedShellTabs),
    }),

  setEmbeddedShellTabs: (tabs) => {
    set({
      _embeddedShellTabs: tabs,
      embeddedTabs: mergeEmbeddedTabs(get()._embeddedRunnerTabs, tabs),
    });
  },
}));

/** One row of the shell's `embedded-browser://tabs` event (title/url only
 *  from shells that carry live display meta). */
export interface EmbeddedShellTab {
  targetId: string;
  active: boolean;
  title?: string;
  url?: string;
}

/**
 * The strip's row source is the SHELL — it owns the tabs, so its event is
 * the truth for which tabs exist, which is visible, and (newer shells) each
 * tab's live title/url. The runner snapshot ENRICHES rows (taskId, favicon,
 * title/url fallback for older shells) — it goes stale the moment the agent
 * session closes, which is exactly when the human is driving (the frozen
 * strip, live 2026-07-16: Google rendered while the strip named the old
 * page, and human-opened tabs never appeared).
 *
 * No shell rows yet (pre-event, plain web, older shell with an empty set) ⇒
 * the runner snapshot renders verbatim — the pre-existing behavior.
 * Runner-only targets are DROPPED when shell rows exist: the shell omits
 * only uncorrelated (just-created) tabs, and a runner row without a shell
 * row is otherwise a closed tab a stale snapshot would resurrect.
 */
function mergeEmbeddedTabs(
  runnerTabs: EmbeddedBrowserTab[],
  shellTabs: EmbeddedShellTab[] | null
): EmbeddedBrowserTab[] {
  if (!shellTabs || shellTabs.length === 0) return runnerTabs;
  const runnerById = new Map(runnerTabs.map((t) => [t.targetId, t]));
  return shellTabs.map((s) => {
    const r = runnerById.get(s.targetId);
    return {
      targetId: s.targetId,
      active: s.active,
      title: s.title || r?.title || "",
      url: s.url || r?.url || "",
      favicon: r?.favicon ?? null,
      taskId: r?.taskId ?? null,
    };
  });
}
